import React, { ReactElement, useEffect, useState } from "react";

import { FormHelperText, Grid, Typography } from "@mui/material";
import { useStateMachine } from "little-state-machine";

import { TextField, CheckField, TableEntries, FormDialog } from "core";

import {
  CLAIM_EDIT_CREATOR_FIELD,
  CLAIM_EDIT_VALIDATOR_FIELD,
} from "master-data/constants";
import { FillFormOptionType } from "master-data/enums";
import {
  MANDATORY_ENTRY_VALIDATION_MESSAGE,
  yesNoOptions,
} from "shared/constants";
import { FormEntriesFieldProps } from "../props";
import { Current, IRevisionEntriesInput, IRevisionInput } from "./types";
import { columns } from "./config";
import { FormSupplierAgreementsEntry } from "../SupplierAgreementsEntry";
import { vatOptions } from "./constants";

export interface FormSupplierAgreementsProps extends FormEntriesFieldProps {
  /**
   * revisionValues
   * */
  revisionValues: IRevisionInput;
  /**
   * active item
   */
  current: Current;
  /**
   * revisionValuesFormDialog
   */
  revisionValuesFormDialog: IRevisionEntriesInput;
  /**
   * errors
   * */
  errors: any;
  /**
   * getValues
   * */
  getValues: any;
  /**
   * setValues
   */
  setValue: any;
  /**
   * revisionValueIds
   */
  revisionValueIds: string[];
  /**
   * Fill form option type
   */
  fillOption: FillFormOptionType;
  /**
   * onEdit
   */
  onEdit: (newCurrent: Current) => void;
}

export const FormSupplierAgreements = ({
  isEdit,
  control,
  errors,
  errorsFormDialog,
  revisionValueIds,
  revisionValues,
  current,
  fields,
  setValue,
  onAdd,
  onEdit,
  onDelete,
  onDialogCancel,
  onDialogSave,
  clearErrors,
  isNewEntry,
  revisionValuesFormDialog,
  isDialogOpen,
  fillOption,
}: FormSupplierAgreementsProps): ReactElement => {
  const {
    state: { profile, clientDocumentData },
  } = useStateMachine();

  const { claims } = profile;
  const { isDocumentEditable } = clientDocumentData;
  const isValidatorFieldDisabled = !claims.includes(CLAIM_EDIT_VALIDATOR_FIELD);
  const isCreatorFieldDisabled = !(
    claims.includes(CLAIM_EDIT_CREATOR_FIELD) && isDocumentEditable
  );
  const [dialogFillOption, setDialogFillOption] =
    useState<FillFormOptionType>();

  // #region Effects
  useEffect(() => {
    if (fillOption) {
      setValue("supplierID", "Supplier ID Test");
      setValue("avb", true);
      setValue("epd", true);
      setValue("msi", true);
    }
  }, [fillOption, setValue]);
  // #endregion Effects

  return (
    <>
      <Grid container spacing={1} direction="column">
        <Grid item>
          <TextField
            name="supplierID"
            id="supplierID"
            label="SupplierID"
            error={!!errors.supplierID?.message}
            helperText={errors.supplierID?.message}
            disabled={!isEdit || isValidatorFieldDisabled}
            control={control}
            revisionValue={revisionValues?.SupplierID}
          />
        </Grid>
        <Grid item>
          <Typography>Agreements under this contract:</Typography>
        </Grid>
        <Grid item>
          <CheckField
            disabled={!isEdit || isCreatorFieldDisabled}
            control={control}
            name="avb"
            label="AVB"
            revisionValue={revisionValues?.AVB}
          />
        </Grid>
        <Grid item>
          <CheckField
            disabled={!isEdit || isCreatorFieldDisabled}
            control={control}
            name="msi"
            label="MSI"
            revisionValue={revisionValues?.MSI}
          />
        </Grid>
        <Grid item>
          <CheckField
            disabled={!isEdit || isCreatorFieldDisabled}
            control={control}
            name="epd"
            label="EPD"
            revisionValue={revisionValues?.EPD}
          />
        </Grid>
        <Grid item xs={12}>
          <FormHelperText id="table-helper-text" error={!!errors.entries}>
            {MANDATORY_ENTRY_VALIDATION_MESSAGE}
          </FormHelperText>
          <TableEntries
            onAdd={onAdd}
            onEdit={onEdit}
            onDelete={onDelete}
            rows={fields}
            isEdit={isEdit}
            control={control}
            columns={columns}
            revisionValueIds={revisionValueIds}
            fieldOptions={{
              deductedFromPayment: yesNoOptions,
              isVATIncluded: vatOptions,
            }}
          />
        </Grid>
      </Grid>
      <FormDialog
        maxWidth="md"
        isEdit={isEdit}
        isNew={isNewEntry}
        open={isDialogOpen}
        onCancel={onDialogCancel}
        onSave={onDialogSave}
        onClose={() => clearErrors("entries")}
        showFillButton={
          isEdit && process.env.REACT_APP_SHOW_FILL_BUTTON === "true"
        }
        showFillMandatoryOption
        onFill={setDialogFillOption}
      >
        <FormSupplierAgreementsEntry
          control={control}
          revisionValues={revisionValuesFormDialog}
          current={current}
          setValue={setValue}
          isEdit={isEdit}
          errors={errorsFormDialog}
          fillOption={dialogFillOption}
          resetFillOption={setDialogFillOption}
          onDialogSave={onDialogSave}
        />
      </FormDialog>
    </>
  );
};

export default FormSupplierAgreements;
